// Content Studio Client Types

// =====================================================
// SOURCE TYPES (NotebookLM-style multi-source support)
// =====================================================

export type SourceType = 'text' | 'pdf' | 'url' | 'file' | 'youtube';

export interface Source {
  id: string;
  type: SourceType;
  title: string;
  content: string;
  url?: string;
  fileName?: string;
  fileSize?: number;
  addedAt: Date;
  wordCount?: number;
  summary?: string;
}

export interface SourceInput {
  type: SourceType;
  content?: string; // For text sources
  url?: string; // For URL/YouTube sources
  file?: File; // For file uploads
  title?: string;
}

// =====================================================
// REQUEST TYPES
// =====================================================

export type ContentStudioAction =
  | 'analyze'
  | 'podcast'
  | 'podcast-script'
  | 'video-podcast'
  | 'slides'
  | 'study-guide'
  | 'quiz'
  | 'grade-quiz'
  | 'estimate-podcast'
  // New NotebookLM-style actions
  | 'flashcards'
  | 'mind-map'
  | 'summary'
  | 'faq'
  | 'timeline'
  | 'briefing-doc'
  | 'fetch-url'
  | 'extract-pdf';

export interface DocumentInput {
  content: string;
  title?: string;
  sources?: Source[]; // Support multiple sources
}

export interface PodcastConfig {
  host1Name?: string;
  host2Name?: string;
  host1VoiceId?: string;
  host2VoiceId?: string;
  host1AvatarUrl?: string;
  host2AvatarUrl?: string;
  style?: 'educational' | 'conversational' | 'interview' | 'debate';
  targetDuration?: '5min' | '10min' | '15min' | '20min';
  includeIntro?: boolean;
  includeOutro?: boolean;
  // Video podcast options
  isVideoPodcast?: boolean;
  avatars?: Array<{
    hostIndex: number;
    avatarId: string;
  }>;
  // Custom avatar uploads (overrides preset avatars when provided)
  host1CustomAvatarUrl?: string;
  host2CustomAvatarUrl?: string;
  // Custom instructions for AI
  customInstructions?: string;
}

export interface QuizConfig {
  questionCount?: number;
  difficulty?: 'easy' | 'medium' | 'hard' | 'mixed';
  types?: Array<'multiple-choice' | 'true-false' | 'short-answer' | 'scenario'>;
}

export interface SlideConfig {
  style?: 'professional' | 'modern' | 'minimal' | 'medical';
  maxSlides?: number;
  brandColor?: string;
  generateImages?: boolean;
  // Custom instructions for AI
  customInstructions?: string;
}

// =====================================================
// OUTPUT LENGTH & GENERATION CONFIG
// =====================================================

export type OutputLength = 'brief' | 'standard' | 'detailed';
export type ContentType = 'healthcare' | 'sales' | 'coaching' | 'general';

export interface GenerationConfig {
  // Content type (determines terminology and style)
  contentType?: ContentType;
  
  // Output length control
  outputLength?: OutputLength;
  
  // Target audience
  audience?: 'student' | 'resident' | 'specialist' | 'general';
  
  // Source selection (IDs of sources to include)
  selectedSourceIds?: string[];
  
  // Focus on specific sections/topics
  focusTopics?: string[];
  
  // Include citations/references
  includeCitations?: boolean;
  
  // Custom instructions
  customInstructions?: string;
}

// Output length presets
export const OUTPUT_LENGTH_PRESETS: Record<OutputLength, {
  label: string;
  description: string;
  wordCount: { summary: number; studyGuide: number };
  flashcardCount: number;
  faqCount: number;
  quizQuestions: number;
}> = {
  brief: {
    label: 'Brief',
    description: '~1 page, key points only',
    wordCount: { summary: 300, studyGuide: 500 },
    flashcardCount: 10,
    faqCount: 5,
    quizQuestions: 5,
  },
  standard: {
    label: 'Standard',
    description: '~2-3 pages, balanced coverage',
    wordCount: { summary: 600, studyGuide: 1200 },
    flashcardCount: 20,
    faqCount: 10,
    quizQuestions: 10,
  },
  detailed: {
    label: 'Detailed',
    description: '~5+ pages, comprehensive',
    wordCount: { summary: 1500, studyGuide: 3000 },
    flashcardCount: 40,
    faqCount: 20,
    quizQuestions: 20,
  },
};

// =====================================================
// RESPONSE TYPES
// =====================================================

export interface ContentStudioResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

// Analysis Response
export interface DocumentAnalysis {
  title: string;
  summary: string;
  keyTopics: Array<{
    topic: string;
    description: string;
    importance: 'high' | 'medium' | 'low';
  }>;
  mainPoints: string[];
  terminology: Array<{
    term: string;
    definition: string;
  }>;
  suggestedQuestions: string[];
  estimatedReadTime: string;
  complexity: 'beginner' | 'intermediate' | 'advanced';
}

// Podcast Response
export interface PodcastSegment {
  speaker: 'host1' | 'host2';
  speakerName: string;
  text: string;
  audioUrl?: string;
  videoUrl?: string; // HeyGen generated video URL
  hostIndex?: number;
}

export interface PodcastScript {
  title: string;
  description: string;
  segments: PodcastSegment[];
  totalDuration?: string;
}

// Video podcast segment
export interface VideoPodcastSegment {
  segmentIndex: number;
  avatarId: string;
  avatarName: string;
  audioUrl: string;
  videoUrl?: string; // HeyGen generated video URL
}

// Video podcast data
export interface VideoPodcastData {
  title: string;
  description: string;
  segments: VideoPodcastSegment[];
  mode: 'avatar-display' | 'full-video';
}

export interface PodcastResult {
  script: PodcastScript;
  audioSegments: Array<{
    segmentIndex: number;
    audioUrl: string;
    speaker: string;
    hostIndex?: number;
    /** Actual audio duration in seconds for accurate avatar sync */
    durationSec?: number;
  }>;
  combinedAudioUrl?: string;
  // Video podcast data
  isVideoPodcast?: boolean;
  videoPodcast?: VideoPodcastData;
}

export interface PodcastEstimate {
  estimatedSegments: number;
  estimatedAudioMinutes: number;
  estimatedGenerationTime: string;
  estimatedCost: string;
}

// Slides Response
export interface Slide {
  slideNumber: number;
  title: string;
  layout: 'title' | 'content' | 'two-column' | 'image-text' | 'bullets' | 'quote';
  content: {
    mainText?: string;
    bullets?: string[];
    leftColumn?: string[];
    rightColumn?: string[];
    quote?: string;
    attribution?: string;
    imagePrompt?: string;
    generatedImageUrl?: string;
  };
  speakerNotes: string;
}

export interface SlidesDeck {
  title: string;
  subtitle?: string;
  author?: string;
  slides: Slide[];
  theme: {
    primaryColor: string;
    style: string;
  };
}

// Study Guide Response
export interface StudyGuideSection {
  title: string;
  content: string;
  keyPoints: string[];
  examples?: string[];
  practiceQuestions?: string[];
}

export interface StudyGuide {
  title: string;
  overview: string;
  learningObjectives: string[];
  sections: StudyGuideSection[];
  quickReference: {
    summary: string;
    keyFacts: string[];
  };
  glossary: Array<{
    term: string;
    definition: string;
  }>;
  additionalResources?: string[];
}

// Quiz Response
export interface QuizQuestion {
  id: string;
  type: 'multiple-choice' | 'true-false' | 'short-answer' | 'scenario';
  difficulty: 'easy' | 'medium' | 'hard';
  topic: string;
  question: string;
  options?: string[];
  correctAnswer: number | string | boolean;
  explanation: string;
  scenarioContext?: string;
}

export interface Quiz {
  title: string;
  description: string;
  totalQuestions: number;
  estimatedTime: string;
  questions: QuizQuestion[];
}

export interface UserAnswer {
  questionId: string;
  answer: number | string | boolean;
}

export interface QuizGradingResult {
  totalQuestions: number;
  correctAnswers: number;
  score: number;
  percentage: number;
  passed: boolean;
  passingScore: number;
  results: Array<{
    questionId: string;
    question: string;
    userAnswer: number | string | boolean;
    correctAnswer: number | string | boolean;
    isCorrect: boolean;
    explanation: string;
  }>;
  feedback: string;
  areasToImprove: string[];
}

// =====================================================
// FLASHCARDS RESPONSE
// =====================================================

export interface Flashcard {
  id: string;
  front: string;
  back: string;
  category?: string;
  difficulty: 'easy' | 'medium' | 'hard';
  tags?: string[];
}

export interface FlashcardDeck {
  title: string;
  description: string;
  totalCards: number;
  cards: Flashcard[];
  categories: string[];
}

// =====================================================
// MIND MAP RESPONSE
// =====================================================

export interface MindMapNode {
  id: string;
  label: string;
  description?: string;
  children?: MindMapNode[];
  color?: string;
}

export interface MindMap {
  title: string;
  centralTopic: string;
  nodes: MindMapNode[];
  connections?: Array<{
    from: string;
    to: string;
    label?: string;
  }>;
}

// =====================================================
// SUMMARY/FAQ RESPONSE
// =====================================================

export interface SummaryResponse {
  title: string;
  executiveSummary: string;
  keyPoints: string[];
  sections: Array<{
    heading: string;
    content: string;
  }>;
  wordCount: number;
}

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category?: string;
}

export interface FAQResponse {
  title: string;
  description: string;
  items: FAQItem[];
  categories: string[];
}

// =====================================================
// TIMELINE RESPONSE
// =====================================================

export interface TimelineEvent {
  id: string;
  date?: string;
  period?: string;
  title: string;
  description: string;
  significance: 'major' | 'minor' | 'contextual';
  category?: string;
}

export interface Timeline {
  title: string;
  description: string;
  events: TimelineEvent[];
  startDate?: string;
  endDate?: string;
}

// =====================================================
// BRIEFING DOC RESPONSE
// =====================================================

export interface BriefingDoc {
  title: string;
  date: string;
  preparedFor?: string;
  executiveSummary: string;
  background: string;
  keyFindings: Array<{
    finding: string;
    importance: 'critical' | 'high' | 'medium' | 'low';
    details?: string;
  }>;
  recommendations: string[];
  nextSteps?: string[];
  appendix?: Array<{
    title: string;
    content: string;
  }>;
}

// =====================================================
// UI STATE TYPES
// =====================================================

export type ContentTab = 'analyze' | 'podcast' | 'slides' | 'study-guide' | 'quiz';

export interface ContentStudioState {
  activeTab: ContentTab;
  documentContent: string;
  documentTitle: string;
  isLoading: boolean;
  error: string | null;

  // Results
  analysis: DocumentAnalysis | null;
  podcast: PodcastResult | null;
  podcastScript: PodcastScript | null;
  slides: SlidesDeck | null;
  studyGuide: StudyGuide | null;
  quiz: Quiz | null;
  quizResult: QuizGradingResult | null;

  // Quiz taking state
  quizAnswers: Map<string, number | string | boolean>;
  isQuizSubmitted: boolean;
}

export type ContentStudioStatus = 'idle' | 'loading' | 'success' | 'error';

// =====================================================
// VOICE OPTIONS FOR PODCAST (NOW FROM DATABASE)
// =====================================================

// Voice agent from database
export interface VoiceAgentOption {
  id: string;
  internal_id: string;
  name: string;
  elevenlabs_voice_id: string | null;
  elevenlabs_agent_id: string | null;
  gender: 'male' | 'female';
  accent: 'AU' | 'US' | 'UK';
  agent_type: 'patient' | 'customer' | 'examiner';
  description: string | null;
  is_active: boolean;
  avatar_url: string | null;
}

// Legacy interface for backwards compatibility
export interface PodcastVoice {
  id: string;
  voiceId: string; // ElevenLabs voice ID
  name: string;
  description: string;
  gender: 'male' | 'female';
  accent: string;
}

// Helper to convert VoiceAgentOption to PodcastVoice format
export function voiceAgentToPodcastVoice(agent: VoiceAgentOption): PodcastVoice | null {
  if (!agent.elevenlabs_voice_id) return null;
  return {
    id: agent.internal_id,
    voiceId: agent.elevenlabs_voice_id,
    name: agent.name,
    description: agent.description || '',
    gender: agent.gender,
    accent: agent.accent === 'AU' ? 'australian' : agent.accent === 'UK' ? 'british' : 'american',
  };
}

// DEPRECATED: Use database query instead
// Keeping empty array for backwards compatibility
export const PODCAST_VOICES: PodcastVoice[] = [];

// Helper function to get voice by ID (deprecated - use database)
export function getVoiceById(id: string): PodcastVoice | undefined {
  return undefined;
}

// Helper function to get voices by gender (deprecated - use database)
export function getVoicesByGender(gender: 'male' | 'female'): PodcastVoice[] {
  return [];
}

// =====================================================
// PRESET AVATAR OPTIONS (Diverse Medical Professionals)
// =====================================================

// Preset avatar for podcast hosts (using Unsplash free images)
export interface PresetAvatar {
  id: string;
  name: string;
  description: string;
  gender: 'male' | 'female';
  ethnicity: 'australian' | 'african' | 'asian' | 'european';
  imageUrl: string;
}

// Diverse preset avatars library - free Unsplash images (no attribution required)
export const PRESET_AVATARS: PresetAvatar[] = [
  // Australian Doctors
  {
    id: 'au-female-doctor',
    name: 'Dr. Sarah Mitchell',
    description: 'Australian Female Doctor',
    gender: 'female',
    ethnicity: 'australian',
    imageUrl: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=400&fit=crop&crop=face',
  },
  {
    id: 'au-male-doctor',
    name: 'Dr. James Wilson',
    description: 'Australian Male Doctor',
    gender: 'male',
    ethnicity: 'australian',
    imageUrl: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&h=400&fit=crop&crop=face',
  },
  // African Doctors
  {
    id: 'af-female-doctor',
    name: 'Dr. Amara Okonkwo',
    description: 'African Female Doctor',
    gender: 'female',
    ethnicity: 'african',
    imageUrl: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=400&fit=crop&crop=face',
  },
  {
    id: 'af-male-doctor',
    name: 'Dr. Kwame Mensah',
    description: 'African Male Doctor',
    gender: 'male',
    ethnicity: 'african',
    imageUrl: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=400&h=400&fit=crop&crop=face',
  },
  // Asian Doctors
  {
    id: 'as-female-doctor',
    name: 'Dr. Mei Lin Chen',
    description: 'Asian Female Doctor',
    gender: 'female',
    ethnicity: 'asian',
    imageUrl: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=400&h=400&fit=crop&crop=face',
  },
  {
    id: 'as-male-doctor',
    name: 'Dr. Hiroshi Tanaka',
    description: 'Asian Male Doctor',
    gender: 'male',
    ethnicity: 'asian',
    imageUrl: 'https://images.unsplash.com/photo-1612531385446-f7e6d131e1d0?w=400&h=400&fit=crop&crop=face',
  },
  // European Doctors
  {
    id: 'eu-female-doctor',
    name: 'Dr. Emma Schmidt',
    description: 'European Female Doctor',
    gender: 'female',
    ethnicity: 'european',
    imageUrl: 'https://images.unsplash.com/photo-1651008376811-b90baee60c1f?w=400&h=400&fit=crop&crop=face',
  },
  {
    id: 'eu-male-doctor',
    name: 'Dr. Marcus Weber',
    description: 'European Male Doctor',
    gender: 'male',
    ethnicity: 'european',
    imageUrl: 'https://images.unsplash.com/photo-1642541724244-83d49288a86b?w=400&h=400&fit=crop&crop=face',
  },
];

// Helper function to get avatar by ID
export function getPresetAvatarById(id: string): PresetAvatar | undefined {
  return PRESET_AVATARS.find(a => a.id === id);
}

// Helper function to get avatars by gender
export function getPresetAvatarsByGender(gender: 'male' | 'female'): PresetAvatar[] {
  return PRESET_AVATARS.filter(a => a.gender === gender);
}

// Helper function to get avatars by ethnicity
export function getPresetAvatarsByEthnicity(ethnicity: PresetAvatar['ethnicity']): PresetAvatar[] {
  return PRESET_AVATARS.filter(a => a.ethnicity === ethnicity);
}

// Default avatars for host1 (female) and host2 (male)
export const DEFAULT_HOST1_AVATAR = PRESET_AVATARS.find(a => a.gender === 'female')!;
export const DEFAULT_HOST2_AVATAR = PRESET_AVATARS.find(a => a.gender === 'male')!;

// =====================================================
// LEGACY VIDEO AVATAR TYPES (Deprecated - kept for compatibility)
// =====================================================

// Video avatar interface (kept for backwards compatibility)
export interface VideoAvatar {
  id: string;
  heygenId: string;
  name: string;
  gender: 'male' | 'female';
  category: 'doctor' | 'business' | 'casual';
}

// Empty array - HeyGen avatars no longer used with Remotion
export const VIDEO_AVATARS: VideoAvatar[] = [];

// Deprecated helper functions
export function getAvatarById(id: string): VideoAvatar | undefined {
  return undefined;
}

export function getAvatarsByGender(gender: 'male' | 'female'): VideoAvatar[] {
  return [];
}

export function getAvatarsByCategory(category: 'doctor' | 'business' | 'casual'): VideoAvatar[] {
  return [];
}
